/**
 * desired-state — コントロールプレーンとノードエージェントの中心的な契約。
 * **ARCHITECTURE.md §3.4 / §4.7 / §4.8 が唯一の正**（設計書 04 の
 * `src/agent/state.ts` は作らない）。
 *
 * 設計上の約束:
 *   1. **全量スナップショット**。差分ではない。エージェントは本体 +
 *      各セクションのページを読めばノードのあるべき姿を完全に再構築できる。
 *   2. `generation` は単調増加。payload が同一（`payloadHash` が同じ）なら
 *      generation は上げない。巻き戻しは「古い payload を**新しい** generation で
 *      再 publish」で表す。世代の単調性は絶対に崩さない。
 *   3. **秘密情報は本体に含めない**。DKIM 秘密鍵・証明書秘密鍵・SRS 秘密は
 *      `{keyId, version}` の参照だけを載せ、実体は
 *      `GET /api/v1/agent/secrets/{dkim|cert|srs}/:id?version=` で個別に取る。
 *      エージェントはそれをディスクキャッシュし、CP 停止中も last-good で動く。
 *      （旧版は payload に平文で載せていた。CP の応答 1 本が全ノードの全ドメインの
 *        署名鍵を運ぶ構造だったため廃止した。）
 *   4. 巨大コレクション（mailboxes 等）は本体に **digest と件数だけ**載せ、
 *      実体は `GET /api/v1/agent/state/{section}` でページ取得する。
 *      digest が前回と同じセクションは取りに行かない。
 *   5. パスワードハッシュは載せる（Dovecot が検証に使う）。**平文は載せない**。
 */
import { z } from 'zod';
import {
  certModeSchema,
  nodeDomainStatusSchema,
  nodeMailboxStatusSchema,
  nodeRoleSchema,
  secretStateSchema,
  spamActionSchema,
} from '../enums.js';
// リリース関連の型は `contracts/release.ts` が正。ここでは re-export しない
// （index.ts が両方を `export *` するため、二重定義で名前が曖昧になる）。
import { maintenanceWindowSchema, releaseChannelSchema, signedReleaseDescriptorSchema } from './release.js';

/** desired-state のスキーマ版。エージェントは一致しなければ適用しない。 */
export const DESIRED_STATE_SCHEMA_VERSION = 1;

/** 10 進の非負整数文字列（bigint を JSON で安全に運ぶための表現）。 */
export const bigintStringSchema = z.string().regex(/^\d+$/, '非負整数の文字列である必要があります');

/** sha256 の hex。 */
export const sha256HexSchema = z.string().regex(/^[a-f0-9]{64}$/, 'sha256 の hex である必要があります');

/* ------------------------------------------------------------ ノード自身 */

export const desiredNodeSchema = z.object({
  /** MX が指す FQDN。HELO 名・既定証明書名・PTR と一致していること。 */
  hostname: z.string().min(1),
  publicIpv4: z.array(z.string().ip({ version: 'v4' })).default([]),
  /**
   * **「送信に使う IPv6」だけを入れる。**「ノードが持っている v6」ではない。
   *
   * この配列の読み手は全員これを出口 IP として扱う:
   *   - サービス SPF の `ip6:`（`buildServiceSpfRecord()`）
   *   - FCrDNS / PTR 検査の対象（`verifyNodeDns()`）
   * 送信に使わない v6（ULA / ブリッジ割当 / 受信専用）を入れると、
   * **直しようのない critical が IP ごと・日ごとに永久に出続ける**。
   * エージェントは `MAILSV_NODE_IPV6_MODE=full` のときだけ値を入れる。
   */
  publicIpv6: z.array(z.string().ip({ version: 'v6' })).default([]),
  role: nodeRoleSchema,
  /** true の間は新規受信を絞り、ロールアウトの対象からも外す。 */
  maintenanceMode: z.boolean(),
  /** ノード全体の送信可否。IP レピュテーション悪化時に CP が落とす。 */
  outboundEnabled: z.boolean(),
});
export type DesiredNode = z.infer<typeof desiredNodeSchema>;

/* -------------------------------------------------------------- リリース */

export const desiredReleaseSchema = z.object({
  channel: releaseChannelSchema,
  /** CP 署名済み blob をそのまま格納する。エージェントは中身を組み立てない。 */
  target: signedReleaseDescriptorSchema,
  rollbackTo: signedReleaseDescriptorSchema.nullable(),
  window: maintenanceWindowSchema.nullable(),
  /** true の間はいかなる更新も適用しない（ロールアウトの緊急停止）。 */
  halted: z.boolean(),
});
export type DesiredRelease = z.infer<typeof desiredReleaseSchema>;

/* ---------------------------------------------------------------- ポリシー */

export const smtpPolicySchema = z.object({
  messageSizeBytesMax: z.number().int().positive(),
  recipientsPerMessageMax: z.number().int().positive(),
  submissionConnectionsPerIpMax: z.number().int().positive(),
  imapConnectionsPerUserMax: z.number().int().positive(),
  /** submission/submissions で TLS を必須にするか。v1 では常に true。 */
  requireTlsForSubmission: z.boolean(),
  /** postscreen の DNSBL 閾値。0 で DNSBL 判定を無効化。 */
  postscreenDnsblThreshold: z.number().int().nonnegative(),
  greylistingEnabled: z.boolean(),
});
export type SmtpPolicy = z.infer<typeof smtpPolicySchema>;

export const outboundRatePolicySchema = z.object({
  /**
   * ノード全体の 1 分あたり送信上限（IP の焼き付き防止）。0 = 無制限。
   * **セレクタは定数（ノード 1 個のバケット）でなければならない。**
   * 受信者ごと・ユーザーごとのバケットに入れると、名前は「ノード全体」なのに
   * 実際はいくらでも並列に出せる。
   */
  nodePerMinuteMax: z.number().int().nonnegative(),
  /**
   * **ノード全体**の 1 日あたり送信上限。0 = 無制限。R0 §2.3 で追加。
   *
   * これが無いと、ウォームアップの上限をメールボックス単位にしか適用できない。
   * Day1 を 50 通に絞ってもメールボックスが 100 個あればノードからは
   * 5,000 通/日出るので、ウォームアップの目的（新規 IP を焼かない）を
   * 一切達成しない。`warmupPerDayMax` が非 null の間はそちらが優先する。
   */
  nodePerDayMax: z.number().int().nonnegative().default(0),
  /** メールボックス側に個別指定が無いときの既定値。 */
  defaultPerHourMax: z.number().int().positive(),
  defaultPerDayMax: z.number().int().positive(),
  /**
   * 新規 IP のウォームアップ中の**ノード全体**の 1 日上限。
   * null ならウォームアップ無し（`nodePerDayMax` が効く）。
   */
  warmupPerDayMax: z.number().int().positive().nullable(),
});
export type OutboundRatePolicy = z.infer<typeof outboundRatePolicySchema>;

export const spamPolicySchema = z.object({
  /** このスコア以上で 550 拒否（受信）。 */
  rejectScore: z.number(),
  /** このスコア以上で Junk へ振り分け。 */
  junkScore: z.number(),
  greylistScore: z.number(),
  /** メールボックス側に指定が無いときの既定動作。 */
  defaultAction: spamActionSchema,
  subjectTag: z.string(),
  /** 送信メールのスコア上限。超えたら 550（§7.4 のアウトバウンド検査）。 */
  outboundRejectScore: z.number(),
});
export type SpamPolicy = z.infer<typeof spamPolicySchema>;

export const antivirusPolicySchema = z.object({
  enabled: z.boolean(),
  /** 検体が見つかったときの動作。 */
  action: z.enum(['reject', 'quarantine', 'tag']),
  maxScanBytes: z.number().int().positive(),
});
export type AntivirusPolicy = z.infer<typeof antivirusPolicySchema>;

export const banPolicySchema = z.object({
  /** この回数の認証失敗が窓の中で起きたら BAN。 */
  authFailureThreshold: z.number().int().positive(),
  authFailureWindowSec: z.number().int().positive(),
  /** 初回の BAN 期間。再犯ごとに倍化する。 */
  banTtlSec: z.number().int().positive(),
  banTtlMaxSec: z.number().int().positive(),
  /**
   * 絶対に BAN してはならない CIDR（CP / 監視 / 顧客申告 IP）。
   * これに加えてエージェントは**自ノードの公開 IP と Docker ブリッジ CIDR を
   * 常時除外**する（自分を BAN するループの防止）。
   */
  allowCidrs: z.array(z.string()).default([]),
});
export type BanPolicy = z.infer<typeof banPolicySchema>;

export const desiredPolicySchema = z.object({
  smtp: smtpPolicySchema,
  outboundRate: outboundRatePolicySchema,
  spam: spamPolicySchema,
  antivirus: antivirusPolicySchema,
  banPolicy: banPolicySchema,
});
export type DesiredPolicy = z.infer<typeof desiredPolicySchema>;

/* -------------------------------------------------------- コレクション参照 */

/** desired-state の本体に載せるセクション名（別ページ取得の対象）。 */
export const DESIRED_STATE_SECTIONS = [
  'domains',
  'mailboxes',
  'aliases',
  'forwarders',
  'catchAlls',
  'senderAcl',
  'sieve',
] as const;
export type DesiredStateSection = (typeof DESIRED_STATE_SECTIONS)[number];
export const desiredStateSectionSchema = z.enum(DESIRED_STATE_SECTIONS);

/**
 * セクションの要約。**本体には digest と件数しか載らない**。
 * digest が前回と同じならエージェントはそのセクションを取りに行かない。
 */
export const collectionRefSchema = z.object({
  /** `sha256(canonicalJson(items))`。items は id 昇順で並べたもの。 */
  digest: sha256HexSchema,
  count: z.number().int().nonnegative(),
});
export type CollectionRef = z.infer<typeof collectionRefSchema>;

export const desiredCollectionsSchema = z.object({
  domains: collectionRefSchema,
  mailboxes: collectionRefSchema,
  aliases: collectionRefSchema,
  forwarders: collectionRefSchema,
  catchAlls: collectionRefSchema,
  senderAcl: collectionRefSchema,
  sieve: collectionRefSchema,
});
export type DesiredCollections = z.infer<typeof desiredCollectionsSchema>;

/* -------------------------------------------------------- 秘密情報の参照 */

export const dkimSecretRefSchema = z.object({
  keyId: z.string().min(1),
  domain: z.string().min(1),
  selector: z.string().min(1).max(63),
  version: z.number().int().positive(),
  /** `retiring` は検証用に残すだけで、新規署名には使わない。 */
  state: secretStateSchema,
});
export type DkimSecretRef = z.infer<typeof dkimSecretRefSchema>;

export const certSecretRefSchema = z.object({
  certId: z.string().min(1),
  /** SAN を含む証明書名の一覧。1 本目が CN。 */
  names: z.array(z.string().min(1)).min(1),
  version: z.number().int().positive(),
  notAfter: z.string().datetime(),
  /** どのサービスに割り当てるか。 */
  usage: z.enum(['mail', 'submission', 'imap', 'default']),
});
export type CertSecretRef = z.infer<typeof certSecretRefSchema>;

export const srsSecretRefSchema = z.object({
  keyId: z.string().min(1),
  version: z.number().int().positive(),
});
export type SrsSecretRef = z.infer<typeof srsSecretRefSchema>;

/**
 * バックアップ用の秘密（R0 §2.4）。
 *
 * restic のリポジトリパスフレーズと S3 キーの**正本は CP** に置く。
 * ノード上のファイルだけに存在すると、ノード全損時に S3 に残った
 * バックアップが復号できないゴミになる（「ノードを全損しても CP から復元できる」
 * という設計前提が実装で成立していない状態だった）。
 */
export const backupSecretRefSchema = z.object({
  kind: z.enum(['restic', 'backup_s3']),
  keyId: z.string().min(1),
  version: z.number().int().positive(),
  /** ノードが書き出すファイルのパス。エージェントはこの場所へ 0600 で置く。 */
  targetPath: z.string().min(1),
});
export type BackupSecretRef = z.infer<typeof backupSecretRefSchema>;

/**
 * バックアップ秘密の `key_id`。**CP と ノードで同じ規則を使う**ことが要。
 * ここを別々に書くと、desired-state に載る keyId と
 * `GET /agent/secrets/restic/:id` が期待する id がずれて 404 になる。
 */
export function resticSecretKeyId(nodeId: string): string {
  return `restic-${nodeId}`;
}

export function backupS3SecretKeyId(nodeId: string): string {
  return `backup-s3-${nodeId}`;
}

/** ノード上の書き出し先。`stack/mailnode` の `backup.sh` が読む場所と一致させる。 */
export const RESTIC_PASSWORD_PATH = '/run/secrets/restic-password';
export const BACKUP_S3_ENV_PATH = '/run/secrets/backup-s3.env';

export const desiredSecretsSchema = z.object({
  /** **そのノードが担当するドメインの鍵だけ**が載る。 */
  dkim: z.array(dkimSecretRefSchema).default([]),
  certs: z.array(certSecretRefSchema).default([]),
  /** 全 MX ノード共通。ノードごとに違うと戻りバウンスが検証できない。 */
  srs: srsSecretRefSchema.nullable(),
  /** restic / S3 の資格情報。空配列なら CP がまだ発行していない。 */
  backup: z.array(backupSecretRefSchema).default([]),
});
export type DesiredSecrets = z.infer<typeof desiredSecretsSchema>;

/* ------------------------------------------------------------ テレメトリ */

export const desiredTelemetrySchema = z.object({
  metricsIntervalSec: z.number().int().positive(),
  statusIntervalSec: z.number().int().positive(),
  /** `doveadm quota get -A` の全件走査間隔。既定 1800 秒。 */
  quotaPollSec: z.number().int().positive(),
});
export type DesiredTelemetry = z.infer<typeof desiredTelemetrySchema>;

/* ------------------------------------------------------------- ブロック */

export const desiredBlocklistEntrySchema = z.object({
  cidr: z.string().min(1),
  /** null = 無期限（CP からの手動遮断のみ）。 */
  expiresAt: z.string().datetime().nullable(),
});
export type DesiredBlocklistEntry = z.infer<typeof desiredBlocklistEntrySchema>;

/* ------------------------------------------------------------ バックアップ */

/**
 * バックアップ先（R0 §2.4「バックアップ先が単一・オフサイト無し」）。
 *
 * **ノードに `forget --prune` をさせない**のがこの型の主眼。
 * ノードが侵害されると、攻撃者はノード上の S3 キーと restic パスフレーズを
 * 両方持っているので、`forget --prune` ひとつでバックアップごと消せる。
 * ランサムウェアの最も典型的な手口なので、破壊的操作は
 * **CP 保管の別クレデンシャル**からしか行えない形にする。
 *
 * `secondary` は別事業者（Wasabi / B2 など）への二次コピー先。
 * **国内商用なら「VPS と別の事業者」に 1 コピーは必須。**
 * 未設定なら `null` を載せる。**架空の値は入れない**（設定されるまで
 * 「二次コピーが無い」ことを CP が警告し続ける）。
 */
export const desiredBackupTargetSchema = z.object({
  /** `s3:https://s3.example.com/bucket/path`。restic の repository 表記。 */
  repository: z.string().min(1),
  /** この先へ書くのに使う秘密の参照（`secrets.backup` の keyId）。 */
  credentialKeyId: z.string().min(1),
  /** append-only な鍵かどうか。false なら CP が警告を上げる。 */
  appendOnly: z.boolean().default(true),
});
export type DesiredBackupTarget = z.infer<typeof desiredBackupTargetSchema>;

export const desiredBackupSchema = z.object({
  enabled: z.boolean().default(false),
  primary: desiredBackupTargetSchema.nullable().default(null),
  /** 二次コピー（別事業者）。null なら未設定。 */
  secondary: desiredBackupTargetSchema.nullable().default(null),
  /**
   * ノード上で `restic forget --prune` を実行してよいか。**常に false**。
   * 型として残しているのは「なぜノードが prune しないのか」を
   * desired-state を読むだけで分かるようにするため。
   */
  allowNodePrune: z.literal(false).default(false),
  /** 復元テストの推奨間隔（日）。過ぎたら CP が警告する。 */
  restoreCheckIntervalDays: z.number().int().positive().default(30),
});
export type DesiredBackup = z.infer<typeof desiredBackupSchema>;

/* =============================================================== 制御文書 */

/**
 * desired-state 本体（制御文書）。
 * サイズは常に数 KB に収まる。実データはセクションのページ取得で運ぶ。
 */
export const desiredStateV1Schema = z.object({
  schemaVersion: z.literal(DESIRED_STATE_SCHEMA_VERSION),
  nodeId: z.string().min(1),
  /**
   * 単調増加する世代。JSON の安全整数内で足りるので number にする
   * （`If-None-Match: "gen-N"` / ETag と同じ値）。
   */
  generation: z.number().int().nonnegative(),
  issuedAt: z.string().datetime(),
  /**
   * この時刻を過ぎたら「CP と連絡が取れていない」と判断する目安。
   * 過ぎても**適用済みの設定は落とさない**（fail-static）。
   */
  staleAfter: z.string().datetime(),
  /** `sha256(canonicalJson(payload))` の hex。エージェントが再計算して突き合わせる。 */
  payloadHash: sha256HexSchema,
  node: desiredNodeSchema,
  release: desiredReleaseSchema,
  policy: desiredPolicySchema,
  collections: desiredCollectionsSchema,
  secrets: desiredSecretsSchema,
  /**
   * バックアップ先。既定値を持たせてあるので、旧世代の payload も
   * そのまま parse できる（署名済みの last-good を無効にしない）。
   */
  backup: desiredBackupSchema.default({
    enabled: false,
    primary: null,
    secondary: null,
    allowNodePrune: false,
    restoreCheckIntervalDays: 30,
  }),
  blocklist: z.array(desiredBlocklistEntrySchema).default([]),
  certMode: certModeSchema,
  telemetry: desiredTelemetrySchema,
});
export type DesiredStateV1 = z.infer<typeof desiredStateV1Schema>;

/** CP の Ed25519 署名を添えた配布形。署名対象は `payloadHash`。 */
export const signedDesiredStateSchema = z.object({
  state: desiredStateV1Schema,
  signature: z.string().min(1),
  /** ローテーション中は 2 本の CP 鍵が併存する。どちらで署名したかを示す。 */
  signingKeyId: z.string().min(1),
});
export type SignedDesiredState = z.infer<typeof signedDesiredStateSchema>;

/* ========================================================= セクション項目 */

/**
 * 契約者がドメイン単位で設定した迷惑メール判定（R132）。
 *
 * 形は `PUT /api/v1/portal/domains/:domainId/spam` の本文（`domains.settings.spam`）
 * と**同じ**にしてある。CP 側は保存した値をそのままここへ載せ、閾値への写像は
 * ノード（`renderRspamdSettings`）で行う。写像を CP でやらないのは、
 * ノード既定の `policy.spam.junkScore` を基準にした相対値だからで、
 * 基準が変わったときに全ドメインを publish し直さずに済む。
 *
 * ⚠ **2026-08-29 まで、この項目は desired-state に 1 つも載っていなかった。**
 * 画面は保存できて 202（「反映しています」）を返すのに、ノードには何も届かず
 * 判定は 1 通も変わらない、という問い合わせても再現しない壊れ方をしていた。
 */
export const desiredDomainSpamSchema = z.object({
  /** false なら「このドメイン宛は迷惑メール判定しない」。 */
  enabled: z.boolean(),
  /** 判定強度。`low` ＝ しきい値が高い（判定されにくい）。 */
  level: z.enum(['low', 'medium', 'high']),
  action: z.enum(['tag', 'move_to_junk', 'reject']),
  /** 常に受け取る差出人（アドレスまたはドメイン）。 */
  allowList: z.array(z.string().min(1).max(320)).max(500),
  /** 常に拒否する差出人（アドレスまたはドメイン）。 */
  denyList: z.array(z.string().min(1).max(320)).max(500),
});
export type DesiredDomainSpam = z.infer<typeof desiredDomainSpamSchema>;

/**
 * ドメインの迷惑メール設定の既定値。**`portal/domains.ts` の
 * `toSpamPolicyView()` と同じ値**にすること（片方だけ変えると
 * 「画面には medium と出ているのにノードは low で動く」になる）。
 */
export const DESIRED_DOMAIN_SPAM_DEFAULT: DesiredDomainSpam = {
  enabled: true,
  level: 'medium',
  action: 'move_to_junk',
  allowList: [],
  denyList: [],
};

export const desiredDomainSchema = z.object({
  id: z.string().min(1),
  organizationId: z.string().min(1),
  name: z.string().min(1),
  status: nodeDomainStatusSchema,
  /** null = キャッチオール無効。有効な場合の宛先アドレス。 */
  catchallTarget: z.string().nullable(),
  /** 現在有効な DKIM セレクタ。null なら署名しない。 */
  dkimSelector: z.string().max(63).nullable(),
  maxMessageBytes: z.number().int().positive(),
  inboundEnabled: z.boolean(),
  /** `domains.dns_status='ok'` になるまで false（§7.4 の送信ゲート）。 */
  outboundEnabled: z.boolean(),
  /**
   * 表示専用。postsrsd はドメイン単位の SRS OFF を表現できないため、
   * 実際の SRS はノード全体で ON になる（§7.4）。
   */
  srsEnabled: z.boolean(),
  /**
   * ドメイン単位の迷惑メール設定。**`.default()` を付けてあるのが要点**で、
   * この項目が無い旧世代のセクションキャッシュ（`store.loadSection`）や
   * last-good もそのまま parse できる。既定を外すと、更新直後の再起動で
   * 「契約に合いません」と世代ごと捨てて last-good も読めなくなる。
   */
  spam: desiredDomainSpamSchema.default(DESIRED_DOMAIN_SPAM_DEFAULT),
});
export type DesiredDomain = z.infer<typeof desiredDomainSchema>;

export const desiredMailboxSchema = z.object({
  id: z.string().min(1),
  domainId: z.string().min(1),
  /** 完全なメールアドレス。ノード DB では UNIQUE。 */
  address: z.string().min(3),
  localPart: z.string().min(1),
  /** `{ARGON2ID}$argon2id$v=19$m=19456,t=2,p=1$...` 形式。平文は決して送らない。 */
  passwordHash: z.string().min(1),
  passwordScheme: z.string().min(1),
  /**
   * **NULL 不可**（§4.7）。state-publish worker が
   * `COALESCE(mailboxes.quota_bytes, plan.mailboxQuotaBytesDefault)` で必ず解決する。
   * `0` は無制限で、プランが許可している場合のみ（`resolveQuotaBytes` で検証）。
   */
  quotaBytes: z.number().int().nonnegative(),
  /** 0 = 通数無制限。 */
  quotaMessages: z.number().int().nonnegative(),
  /** **相対**パス `<domain>/<local_part>/`。エージェントがルートを前置する（§4.8）。 */
  maildirPath: z.string().min(1),
  status: nodeMailboxStatusSchema,
  allowImap: z.boolean(),
  allowPop3: z.boolean(),
  allowSubmission: z.boolean(),
  sendLimitHour: z.number().int().positive(),
  sendLimitDay: z.number().int().positive(),
  rcptLimitMsg: z.number().int().positive(),
  spamActionScore: z.number(),
  greylisting: z.boolean(),
});
export type DesiredMailbox = z.infer<typeof desiredMailboxSchema>;

export const desiredAliasSchema = z.object({
  id: z.string().min(1),
  domainId: z.string().min(1),
  /** 転送元アドレス。 */
  source: z.string().min(3),
  /** 転送先アドレス。1 行 1 宛先（ノード DB がそう持つ）。 */
  destination: z.string().min(3),
  /** true なら元のメールボックスにも残す。 */
  keepCopy: z.boolean(),
  active: z.boolean(),
});
export type DesiredAlias = z.infer<typeof desiredAliasSchema>;

/**
 * メールボックス由来の転送設定。ノード DB では `aliases` に合流するが、
 * 出所（契約者が設定した転送 vs 運営が作ったエイリアス）を区別して運べるよう
 * セクションを分けている。
 */
export const desiredForwarderSchema = z.object({
  id: z.string().min(1),
  mailboxId: z.string().min(1),
  domainId: z.string().min(1),
  source: z.string().min(3),
  destination: z.string().min(3),
  keepCopy: z.boolean(),
  active: z.boolean(),
});
export type DesiredForwarder = z.infer<typeof desiredForwarderSchema>;

export const desiredCatchAllSchema = z.object({
  domainId: z.string().min(1),
  domain: z.string().min(1),
  /** `@domain` 宛の未知のローカルパートを受ける先。 */
  target: z.string().min(3),
});
export type DesiredCatchAll = z.infer<typeof desiredCatchAllSchema>;

/**
 * `smtpd_sender_login_maps` の元データ。
 * **CP のプロビジョニングで自分自身のアドレス行が必ず 1 行入る**
 * （DB トリガで自動投入し、欠落を構造的に不可能にする。§7.4）。
 */
export const desiredSenderAclSchema = z.object({
  id: z.string().min(1),
  mailboxId: z.string().min(1),
  /** このメールボックスが MAIL FROM に使ってよいアドレス。 */
  sender: z.string().min(3),
});
export type DesiredSenderAcl = z.infer<typeof desiredSenderAclSchema>;

export const desiredSieveSchema = z.object({
  mailboxId: z.string().min(1),
  domain: z.string().min(1),
  localPart: z.string().min(1),
  /**
   * CP が自動応答・転送設定から生成した Sieve。
   * `sieve_before` に置いて無条件先行実行する。エージェントは中身を解釈しない。
   */
  script: z.string(),
});
export type DesiredSieve = z.infer<typeof desiredSieveSchema>;

/** セクション名 → 項目スキーマ。ページ取得の検証に使う。 */
export const DESIRED_STATE_SECTION_SCHEMAS = {
  domains: desiredDomainSchema,
  mailboxes: desiredMailboxSchema,
  aliases: desiredAliasSchema,
  forwarders: desiredForwarderSchema,
  catchAlls: desiredCatchAllSchema,
  senderAcl: desiredSenderAclSchema,
  sieve: desiredSieveSchema,
} as const;

export interface DesiredStateSectionItemMap {
  domains: DesiredDomain;
  mailboxes: DesiredMailbox;
  aliases: DesiredAlias;
  forwarders: DesiredForwarder;
  catchAlls: DesiredCatchAll;
  senderAcl: DesiredSenderAcl;
  sieve: DesiredSieve;
}

/* --------------------------------------------- ドメインの迷惑メール設定の解決 */

/**
 * `domains.settings.spam`（契約者が画面から保存した jsonb）を desired-state の
 * 契約へ写す（R132）。
 *
 * **2 つの publisher（`apps/worker` の state-sections と `apps/api` の
 * desired-state サービス）が同じ値を出すために、写像は必ずここを通す。**
 * 片方だけ独自に写すと、初回 pull と 2 回目以降で section digest が変わり、
 * エージェントが毎回セクションを取り直すことになる。
 *
 * **壊れた JSON を理由に publish を止めない。** `settings` は jsonb で、
 * 過去の版や手作業の投入で形が揃っていないことがある。1 ドメインの設定不備で
 * ノード全体の desired-state が生成できなくなるほうが害が大きいので、parse に
 * 失敗したら既定値（＝画面が「未設定」のときに見せている値）へ落とす。
 * 既定は「有効・medium・Junk へ振り分け」で、緩める側ではない。
 */
export function resolveDomainSpam(settings: unknown): DesiredDomainSpam {
  const raw = (settings as Record<string, unknown> | null | undefined)?.['spam'];
  if (raw === undefined || raw === null) return DESIRED_DOMAIN_SPAM_DEFAULT;
  const parsed = desiredDomainSpamSchema.safeParse({
    ...DESIRED_DOMAIN_SPAM_DEFAULT,
    ...(raw as Record<string, unknown>),
  });
  if (!parsed.success) return DESIRED_DOMAIN_SPAM_DEFAULT;
  return {
    ...parsed.data,
    // digest を安定させるため、リストは正規化して並べ替える。並びが揺れると
    // 「中身は同じなのに毎回セクションを取り直す」ループになる。
    allowList: normalizeSpamList(parsed.data.allowList),
    denyList: normalizeSpamList(parsed.data.denyList),
  };
}

function normalizeSpamList(values: readonly string[]): string[] {
  const out = new Set<string>();
  for (const value of values) {
    const trimmed = value.trim().toLowerCase();
    if (trimmed.length > 0) out.add(trimmed);
  }
  return [...out].sort();
}

/**
 * 判定強度 → ノード既定 `policy.spam.junkScore` からのオフセット。
 *
 * 数字が大きいほど「判定されにくい」。`low`（＝弱め）で +3、`high`（＝強め）で
 * -2 と非対称にしてあるのは、**強めた側の誤判定のほうが被害が大きい**ため
 * （正規のメールが Junk に落ちる）。緩める側は迷惑メールが 1 通届くだけで済む。
 */
export const DOMAIN_SPAM_LEVEL_OFFSET: Readonly<Record<DesiredDomainSpam['level'], number>> = {
  low: 3,
  medium: 0,
  high: -2,
};

/**
 * 「判定しない」を表す実効スコア。閾値を **消す**（`null`）のではなく
 * 到達不能な高さに置くのが要点。rspamd はアクションの閾値が消えると
 * モジュール側の「スコアによる免除」の枝ごと消えることがあり
 * （`plugins/greylist.lua` で実際に踏んでいる）、意図と逆の意味になる。
 */
export const DOMAIN_SPAM_DISABLED_SCORE = 999;

export interface DomainSpamThresholds {
  /** この点数以上で Junk 扱い（rspamd の `add_header`）。 */
  addHeader: number;
  /**
   * この点数以上で 550 拒否。`null` なら**ノード既定のまま**にする。
   * 契約者が下げられるのは Junk の閾値だけで、拒否の閾値は共有 IP の
   * 評判に関わるため事業者の責任範囲（`mailboxSpamOverrides` と同じ方針）。
   */
  reject: number | null;
  /** 迷惑メール判定そのものを止めるか（`enabled === false`）。 */
  disabled: boolean;
}

/**
 * ドメインの迷惑メール設定を rspamd の実効閾値へ写す。
 *
 * **ノード側（`renderRspamdSettings`）から呼ぶ。** CP で写すと、ノード既定の
 * `junkScore` を変えたときに全ドメインを publish し直す必要が出る。
 */
export function resolveDomainSpamThresholds(
  spam: DesiredDomainSpam,
  policy: { junkScore: number; rejectScore: number },
): DomainSpamThresholds {
  if (!spam.enabled) {
    return { addHeader: DOMAIN_SPAM_DISABLED_SCORE, reject: null, disabled: true };
  }
  const base = Number.isFinite(policy.junkScore) ? policy.junkScore : 6;
  // 1 未満にはしない。0 以下だとスコアがマイナスの正規メールまで Junk になる。
  const addHeader = Math.max(1, base + DOMAIN_SPAM_LEVEL_OFFSET[spam.level]);
  // 拒否は「契約者が reject を選んだとき」だけ動かし、**ノード既定の
  // junkScore より下げない**。下げられると正規のメールを 550 で跳ね返せる。
  const reject = spam.action === 'reject' ? Math.max(addHeader, base) : null;
  return { addHeader, reject, disabled: false };
}

/* ------------------------------------------------------------ クォータ解決 */

/**
 * quota の NULL を desired-state の境界の**手前**で潰す（§4.7）。
 *
 * `0`（無制限）はプランが許可している場合のみ通す。zod の `superRefine` では
 * プラン情報が見えないため、解決時点で検査するのが唯一の確実な場所になる。
 * ここを緩めると「無制限のつもりが無かったメールボックスが 1 台のノードの
 * ディスクを食い尽くす」事故に直結する。
 */
export function resolveQuotaBytes(
  mailboxQuotaBytes: number | null,
  planDefaultQuotaBytes: number,
  planAllowsUnlimited: boolean,
): number {
  const resolved = mailboxQuotaBytes ?? planDefaultQuotaBytes;
  if (!Number.isInteger(resolved) || resolved < 0) {
    throw new Error(`クォータ値が不正です: ${String(resolved)}`);
  }
  if (resolved === 0 && !planAllowsUnlimited) {
    throw new Error('このプランでは無制限クォータ（0）を指定できません');
  }
  return resolved;
}
